import { FC } from 'react';
import { Clock, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { TherapistStatusBadge } from './TherapistStatusBadge';
import type {
  TherapistStatusHistoryEntry,
  TherapistStatus,
} from '../../../api/adminTherapistApi';
import type { Pagination } from '../../../types/admin.types';

interface TherapistStatusHistoryProps {
  history: TherapistStatusHistoryEntry[];
  pagination?: Pagination;
  isLoading?: boolean;
  onPageChange?: (page: number) => void;
}

export const TherapistStatusHistory: FC<TherapistStatusHistoryProps> = ({
  history,
  pagination,
  isLoading = false,
  onPageChange,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse flex gap-4">
            <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-slate-700" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-slate-700" />
              <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-slate-700" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-slate-400">
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No status history available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-slate-700" />

        <div className="space-y-6">
          {history.map((entry) => (
            <div key={entry.id} className="relative flex gap-4">
              {/* Timeline dot */}
              <div className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700">
                <div className="h-3 w-3 rounded-full bg-blue-500" />
              </div>

              {/* Content */}
              <div className="flex-1 pb-4">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {entry.previousStatus ? (
                    <>
                      <TherapistStatusBadge
                        status={entry.previousStatus as TherapistStatus}
                        size="sm"
                      />
                      <ArrowRight className="h-4 w-4 text-gray-400 dark:text-slate-500" />
                    </>
                  ) : (
                    <span className="text-xs text-gray-500 dark:text-slate-400 italic">
                      Initial status:
                    </span>
                  )}
                  <TherapistStatusBadge status={entry.newStatus as TherapistStatus} size="sm" />
                </div>

                <p className="text-sm text-gray-600 dark:text-slate-400">
                  Changed by{' '}
                  <span className="font-medium text-gray-900 dark:text-slate-200">
                    {entry.changedByUser?.name || 'System'}
                  </span>
                </p>

                {(entry.reason || entry.notes) && (
                  <div className="mt-2 p-3 rounded-lg bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-700">
                    {entry.reason && (
                      <p className="text-sm text-gray-700 dark:text-slate-300">
                        <span className="font-medium">Reason:</span> {entry.reason}
                      </p>
                    )}
                    {entry.notes && (
                      <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">
                        <span className="font-medium">Notes:</span> {entry.notes}
                      </p>
                    )}
                  </div>
                )}

                <p className="mt-2 text-xs text-gray-400 dark:text-slate-500">
                  {new Date(entry.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TherapistStatusHistory;
